import { type FC, useRef, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Icon from './Icon'
import { useClickOutside } from '../../hooks/useClickOutside'

export interface SelectOption {
  value: string | undefined
  label: string
}

interface SelectProps {
  type?: 'filter' | 'field'
  filterNameKey: string
  options: SelectOption[]
  onChange: (value: string | undefined) => void
  error?: string | undefined
  translateLabel?: boolean
  defaultOption?: SelectOption | undefined
}

const Select: FC<SelectProps> = ({
  type = 'filter',
  filterNameKey,
  options,
  onChange,
  error,
  translateLabel = true,
  defaultOption,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedOption, setSelectedOption] = useState<
    SelectOption | undefined
  >(defaultOption)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const { t } = useTranslation()

  useClickOutside({
    itemRef: dropdownRef,
    triggerRef: buttonRef,
    isOpen,
    setIsOpen,
  })

  useEffect(() => {
    setSelectedOption(defaultOption)
  }, [defaultOption])

  const onSelectOption = (option: SelectOption) => {
    setSelectedOption(option)
    onChange(option.value ? option.value : undefined)
    setIsOpen(false)
  }

  return (
    <div className="relative inline-block w-full" ref={dropdownRef}>
      <button
        type="button"
        ref={buttonRef}
        className={`inline-flex h-9 w-full items-center justify-between gap-2 rounded border border-zinc-500 bg-transparent px-4 py-2 text-sm leading-5 text-white hover:bg-white/10 ${
          type === 'field' ? 'font-medium' : 'font-semibold'
        } ${error ? 'border-red-500' : 'border-zinc-500'}`}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <div className="flex items-center gap-2 truncate">
          {selectedOption?.value
            ? translateLabel
              ? t(`ls_filter_${selectedOption.label}`)
              : selectedOption.label
            : t(filterNameKey)}
        </div>
        <Icon name="chevronDown" width={16} />
      </button>

      {isOpen && (
        <div className="absolute z-10 mt-2 max-h-96 w-full cursor-pointer overflow-y-auto rounded-md border border-zinc-700 bg-neutral-900 shadow-lg ring-1 ring-black/5">
          {options.map((option, index) => (
            <button
              type="button"
              key={index}
              onClick={() => onSelectOption(option)}
              className="flex w-full items-center justify-between px-4 py-2 text-sm capitalize text-white hover:bg-zinc-700"
            >
              <span
                className="block w-full truncate text-left"
                title={
                  translateLabel ? t(`ls_filter_${option.label}`) : option.label
                }
              >
                {translateLabel ? t(`ls_filter_${option.label}`) : option.label}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default Select
